import { useState } from "react";
import { fetchLinkPreview } from "../lib/api";
import type { SectionArticle } from "../lib/segment-types";
import { newSectionId } from "../lib/sections";

/**
 * Paste an Altibbi article's address and have its title, picture and summary
 * filled in from the page itself.
 *
 * The catalogue picker is the usual route; this is for an article the search
 * has not indexed yet. Nothing is written until the fetch succeeds, so a
 * failed lookup never leaves a half-filled section behind.
 */
export function ArticleUrlBox({
  onAdd, disabled,
}: {
  onAdd: (article: SectionArticle) => void;
  disabled?: boolean;
}) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    const target = url.trim();
    if (!target || busy) return;
    setBusy(true);
    setError(null);
    try {
      const meta = await fetchLinkPreview(target);
      onAdd({
        id: newSectionId(),
        title: meta.title || target,
        url: meta.url || target,
        image: meta.image,
        excerpt: meta.excerpt,
      });
      setUrl("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read that page");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nl-urlbox">
      <div className="nl-urlbox-row">
        <input
          className="nl-input"
          type="url"
          inputMode="url"
          placeholder="https://altibbi.com/…"
          value={url}
          disabled={disabled || busy}
          onChange={(e) => { setUrl(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void run(); } }}
        />
        <button
          type="button"
          className="nl-picker-add"
          disabled={disabled || busy || !url.trim()}
          onClick={() => void run()}
        >
          {busy ? "Reading…" : "Fetch"}
        </button>
      </div>
      {error
        ? <p className="nl-urlbox-error">{error}</p>
        : <p className="nl-urlbox-hint">The title, picture and summary are read from the page.</p>}
    </div>
  );
}
